import React, { Fragment } from 'react'
import { compact, map, flatten, filter, round, sum, subtract } from 'lodash'
import Table from 'UI/Table'
import { filteredClusterData, filteredVMs, nPlus1, sumVMData, availableCPU, availableMemory } from './Data'
import { VMTable } from './VMTable'
import { humanizeGB } from 'Decorators/SizeDecorator'
import AssetNameWithLabel from 'FoundationalComponents/AssetNameWithLabel'

export const ClusterTable = ({ clusters, filters, changeFilters, defaultPageSize, isTopLevel = true }) => {
  const data = filteredClusterData({ clusters, filters })

  return (
    <Table {...{
      data: filter(data, (cluster) => filters.powerState === 'All' ? true : filteredVMs(cluster.vms, filters).length > 0),
      stripedColumns: true,
      columns: compact([
        { Header: 'Cluster', columns: [
          { Header: 'Location', id: 'location_name', accessor: (cluster) => (cluster.location_name || '').replace(' Datacentre', '') },
          { Header: 'Name', accessor: 'name', filterable: true, width: 300, Cell: ({ value, original }) => (<AssetNameWithLabel name={value} label={original.asset_label} />) }
        ]},
        { Header: 'Total Capacity', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', columns: flatten([
          { Header: 'Host count', type: 'number', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', accessor: 'host_count' },
          (filters.nPlus ? [
            {
              Header: `Total Cores (N+${filters.nPlus})`, type: 'number', id: 'cpu_n1', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd',
              accessor: cluster => nPlus1(cluster.host_cpu_cores, cluster.host_count, filters.ratio)
            },
            {
              Header: `Memory (N+${filters.nPlus})`, id: 'n1_cluster_memory_gb', type: 'number', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd',
              accessor: (cluster) => nPlus1(cluster.host_memory_total_gb, cluster.host_count, 1), Cell: ({ value }) => humanizeGB(value)
            },
          ] : [
              { Header: 'Total Cores', type: 'number', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', id: 'host_cpu_cores', accessor: cluster => cluster.host_cpu_cores * filters.ratio },
            {
              Header: 'Memory', id: 'cluster_total_memory_gb', type: 'number', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd',
              accessor: 'host_memory_total_gb', Cell: ({ value }) => humanizeGB(value)
            },
          ])
        ])},
        { Header: 'Usage', columns: [
          { Header: 'VM count', type: 'number', id: 'vm_count', accessor: (cluster) => filteredVMs(cluster.vms, filters).length },
          { Header: 'Allocated CPU (cores)', type: 'number', id: 'num_cpu', accessor: (cluster) => sumVMData(cluster.vms, filters, 'num_cpu') },
          { Header: 'Allocated Memory', id: 'cluster_allocated_memory_gb', type: 'number', accessor: (cluster) => sumVMData(cluster.vms, filters, 'memory_gb'), Cell: ({ value }) => humanizeGB(value) },
          { Header: 'Avg CPU usage', type: 'percent', id: 'average_cpu_percent', accessor: (cluster) => {
            return round(round(parseFloat(cluster.host_cpu_usage_mhz) / cluster.host_cpu_total_mhz, 2) * 100)
          }},
          { Header: 'Avg memory usage', type: 'percent', id: 'average_memory_percent', accessor: (cluster) => {
            return round(round(parseFloat(cluster.host_memory_usage_gb) / cluster.host_memory_total_gb, 2) * 100)
          }},
        ]},
        { Header: 'Available', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', columns: [
          { Header: 'CPU (cores)', type: 'number', id: 'available_cpu_n1', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd',
            accessor: (cluster) => availableCPU({ cluster, filters })
          },
          { Header: 'Memory', id: 'n1_available_cluster_memory_gb', type: 'number', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', accessor: (cluster) => {
            return availableMemory({ cluster, filters })
          }, Cell: ({ value }) => humanizeGB(value) },
          { Header: 'CPU %', type: 'percent', rag: true, ragLimits: { red: 10, amber: 25 }, id: 'available_cpu_percent', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd',
            accessor: (cluster) => {
              const clusterCPUCores = filters.nPlus ? nPlus1(cluster.host_cpu_cores, cluster.host_count, filters.ratio) : (cluster.host_cpu_cores * filters.ratio)

              return round((parseFloat(availableCPU({ cluster, filters })) / clusterCPUCores) * 100, 2)
            }
          },
          { Header: 'Memory %', rag: true, ragLimits: { red: 10, amber: 25 }, type: 'percent', id: 'available_memory_percent', headerClassName: 'react-table-cell--odd', className: 'react-table-cell--odd', accessor: (cluster) => {
            const clusterMemoryGB = filters.nPlus ? nPlus1(cluster.host_memory_total_gb, cluster.host_count, 1) : cluster.host_memory_total_gb

            return round((parseFloat(availableMemory({ cluster, filters })) / clusterMemoryGB) * 100, 2)
          }},
        ]}
      ]),
      tableProps: {
        defaultPageSize,
        showPagination: isTopLevel,
        defaultSorted: [{ id: 'location_name', asc: true }],
        SubComponent: ({ original: cluster }) => <VMTable {...{
          showCustomer: isTopLevel,
          vms: filteredVMs(cluster.vms, filters)
        }} />
      }
    }} />
  )
}
